import { useMutation } from 'react-query';
import { useWatch } from 'react-hook-form';
import { Box, Divider, HStack, ScrollView, Select, VStack } from 'native-base';
import { Locale } from 'locale-enum';
import { useTranslation } from 'react-i18next';

import { Button, ControlInput, SelectInput, Text } from '@pimm/base';
import { AddUpdateUserProfile, GetUserProfile } from '@pimm/services/lib/sms-tenants/services';
import { AddUpdateUserProfileRequest, CultureData, UserProfileDto } from '@pimm/services/lib/sms-tenants';
import { useModalFocus } from '@app/hooks/modal-focus.hook';
import { useFormUserInformation } from '../hooks';
import DropdownCultures from './dropdown-cultures';
import { ModalConfirm } from './modal-confirm';
import { ListItem } from './list-item';

export type UserInformationChangeEvent = { name: 'update-profile'; data?: UserProfileDto };

type FormUserInformationProps = {
  cultures?: CultureData[];
  isEditable?: boolean;
  user?: UserProfileDto;
  onClose?: () => void;
  onEndChange: (changeEvent: UserInformationChangeEvent) => void;
};

export const FormUserInformation = ({ cultures, isEditable, user, ...props }: FormUserInformationProps) => {
  const modalConfirm = useModalFocus();
  const { t } = useTranslation();
  const form = useFormUserInformation({
    defaultValues: {
      defaultSiteId: user?.defaultSiteId,
      defaultLanguage: user?.culture ?? Locale.en_US,
      firstName: user?.firstName,
      lastName: user?.lastName,
      jobTitle: user?.jobTitle,
      username: user?.userName,
      role: [user?.roles ?? []].join(' '), // TODO: need to revisit
      contactDetails: {
        email: user?.contactEmail ?? '',
        mobile: user?.mobilePhone ?? '',
        phone: user?.officePhone ?? '',
      },
    },
  });
  const culture = useWatch({ control: form.control, name: 'defaultLanguage' });

  const addUpdateProfile = useMutation({
    mutationFn: async (payload: AddUpdateUserProfileRequest) => {
      await AddUpdateUserProfile(payload);
      // Get profile after successful update
      const profile = await GetUserProfile({ userId: payload.userId });
      return profile;
    },
    onSuccess: profile => props.onEndChange({ name: 'update-profile', data: profile }),
  });

  const handleConfirmChanges = () => {
    if (!isEditable) return false;

    const formValues = form.getValues();
    const payload: AddUpdateUserProfileRequest = {
      userId: user?.userId,
      defaultSiteId: formValues.defaultSiteId,
      culture: formValues.defaultLanguage,
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      jobTitle: formValues.jobTitle,
      contactEmail: formValues.contactDetails.email ?? '',
      mobilePhone: formValues.contactDetails.mobile ?? '',
      officePhone: formValues.contactDetails.phone ?? '',
    };
    addUpdateProfile.mutate(payload);
    modalConfirm.setHide();
  };

  return (
    <VStack h="full" bg="gray.25" borderBottomRightRadius="xl" overflow="hidden">
      <ScrollView h="full">
        <VStack space={4} p={4}>
          {/* User Defaults */}
          <ListItem text={t('account_settings:user_defaults')}>
            <HStack space={3} flex={1}>
              <Box flex={1} justifyContent="center" py={3} px={4} borderRadius="lg" borderWidth={1} borderColor="gray.200" bgColor="white">
                <VStack space={1} zIndex={1000}>
                  <ControlInput
                    control={form.control}
                    name="defaultSiteId"
                    label={t('account_settings:default_site_access')}
                    placeholder={t('account_settings:choose_default_site_access')}
                    disabled={!isEditable}
                    element={
                      <SelectInput _actionSheetContent={{ bgColor: 'rose.100' }}>
                        {user?.sites?.map(_site => (
                          <Select.Item key={_site.id} value={_site.id!} label={_site.name ?? ''} />
                        ))}
                      </SelectInput>
                    }
                    renderErrorText={_err => t(_err ?? '')}
                  />
                </VStack>
              </Box>
              <Box py={3} px={4} w="2/6" borderRadius="lg" justifyContent="center" bgColor="white" borderWidth={1} borderColor="gray.200">
                <VStack zIndex={1000}>
                  <Text size="md" fontWeight={500} color="gray.700" mb={0.5}>
                    {t('account_settings:language')}
                  </Text>
                  <DropdownCultures
                    defaultCulture={culture}
                    cultures={cultures}
                    disabled={!isEditable}
                    onPressCulture={_culture => form.setValue('defaultLanguage', _culture, { shouldDirty: true })}
                  />
                </VStack>
              </Box>
            </HStack>
          </ListItem>

          <Divider bg="gray.200" />

          {/* PIMM info */}
          <ListItem text={t('account_settings:pimm_info')}>
            <Box flex={1} py={3} px={4} borderRadius="lg" justifyContent="center" bgColor="white" borderWidth={1} borderColor="gray.200">
              <VStack space={2}>
                <HStack space={3}>
                  <Box flex={1}>
                    <ControlInput
                      control={form.control}
                      label={t('account_settings:first_name')}
                      name="firstName"
                      disabled={!isEditable}
                      renderErrorText={_err => t(_err ?? '')}
                    />
                  </Box>
                  <Box flex={1}>
                    <ControlInput
                      control={form.control}
                      label={t('account_settings:last_name')}
                      name="lastName"
                      disabled={!isEditable}
                      renderErrorText={_err => t(_err ?? '')}
                    />
                  </Box>
                </HStack>

                <Box flex={1}>
                  <ControlInput
                    control={form.control}
                    name="jobTitle"
                    label={t('account_settings:job_title')}
                    disabled={!isEditable}
                    renderErrorText={_err => t(_err ?? '')}
                  />
                </Box>
                <Box flex={1}>
                  <ControlInput
                    control={form.control}
                    disabled
                    name="role"
                    label={t('account_settings:user_roles')}
                    renderErrorText={_err => t(_err ?? '')}
                  />
                </Box>
                <Box flex={1}>
                  <ControlInput
                    control={form.control}
                    disabled
                    name="username"
                    label={t('account_settings:username')}
                    renderErrorText={_err => t(_err ?? '')}
                  />
                </Box>
              </VStack>
            </Box>
          </ListItem>

          <Divider bg="gray.200" />

          {/* Contact info */}
          <ListItem text={t('account_settings:contact_info')}>
            <Box flex={1} py={3} px={4} borderRadius="lg" justifyContent="center" bgColor="white" borderWidth={1} borderColor="gray.200">
              <VStack space={3}>
                <VStack flex={1} space={2}>
                  <ControlInput
                    control={form.control}
                    name="contactDetails.email"
                    label={`${t('account_settings:contact_email')} *`}
                    disabled={!isEditable}
                    _input={{ autoCapitalize: 'none' }}
                    renderErrorText={_err => t(_err ?? '')}
                  />
                  <Text size="md" fontWeight={500} color="gray.700" lineHeight="sm">
                    {t('account_settings:enter_valid_email_note')}
                  </Text>
                </VStack>
                <HStack space={2}>
                  <Box flex={1}>
                    <ControlInput
                      control={form.control}
                      name="contactDetails.mobile"
                      label={t('account_settings:mobile_phone')}
                      disabled={!isEditable}
                      renderErrorText={_err => t(_err ?? '')}
                    />
                  </Box>
                  <Box flex={1}>
                    <ControlInput
                      control={form.control}
                      name="contactDetails.phone"
                      label={t('account_settings:office_phone')}
                      disabled={!isEditable}
                      renderErrorText={_err => t(_err ?? '')}
                    />
                  </Box>
                </HStack>
              </VStack>
            </Box>
          </ListItem>
        </VStack>
      </ScrollView>

      {isEditable && (
        <HStack space={3} alignItems="center" justifyContent="flex-end" py={3} px={4} borderTopWidth={1}>
          <Button variant="unstyled" minWidth={120} disabled={addUpdateProfile.isLoading} onPress={props.onClose}>
            {t('account_settings:cancel')}
          </Button>

          <Button
            minWidth={120}
            isLoading={addUpdateProfile.isLoading}
            disabled={!form.formState.isDirty}
            onPress={form.handleSubmit(modalConfirm.setOpen)}
          >
            {t('account_settings:save_changes')}
          </Button>
        </HStack>
      )}

      <ModalConfirm isOpen={modalConfirm.isOpen} onClose={modalConfirm.setHide} onConfirm={handleConfirmChanges} />
    </VStack>
  );
};
